import {
  DEFAULT_TTL,
  hasOwn
} from "./chunk-MIYN75UK.js";
import "./chunk-SKR5JO2A.js";

// ../zero-react/src/use-query.tsx
import { useSyncExternalStore } from "react";

// ../shared/src/deep-clone.ts
function deepClone(value) {
  const seen = [];
  return internalDeepClone(value, seen);
}
function internalDeepClone(value, seen) {
  switch (typeof value) {
    case "boolean":
    case "number":
    case "string":
    case "undefined":
      return value;
    case "object": {
      if (value === null) {
        return null;
      }
      if (seen.includes(value)) {
        throw new Error("Cyclic object");
      }
      seen.push(value);
      if (Array.isArray(value)) {
        const rv = value.map((v) => internalDeepClone(v, seen));
        seen.pop();
        return rv;
      }
      const obj = {};
      for (const k in value) {
        if (hasOwn(value, k)) {
          const v = value[k];
          if (v !== void 0) {
            obj[k] = internalDeepClone(v, seen);
          }
        }
      }
      seen.pop();
      return obj;
    }
    default:
      throw new Error(`Invalid type: ${typeof value}`);
  }
}

// ../zero-react/src/use-zero.tsx
import { createContext, useContext } from "react";
import { jsx } from "react/jsx-runtime";
var ZeroContext = createContext(void 0);
function useZero() {
  const zero = useContext(ZeroContext);
  if (zero === void 0) {
    throw new Error("useZero must be used within a ZeroProvider");
  }
  return zero;
}
function createUseZero() {
  return () => useZero();
}
function ZeroProvider({ children, zero }) {
  return /* @__PURE__ */ jsx(ZeroContext.Provider, { value: zero, children });
}

// ../zero-react/src/use-query.tsx
function useQuery(query, options) {
  let enabled = true;
  let ttl = DEFAULT_TTL;
  if (typeof options === "boolean") {
    enabled = options;
  } else if (options) {
    ({ enabled = true, ttl = DEFAULT_TTL } = options);
  }
  const z = useZero();
  const view = viewStore.getView(
    z.clientID,
    query,
    enabled,
    ttl
  );
  return useSyncExternalStore(
    view.subscribeReactInternals,
    view.getSnapshot,
    view.getSnapshot
  );
}
var emptyArray = [];
var disabledSubscriber = () => () => {
};
var resultTypeUnknown = { type: "unknown" };
var resultTypeComplete = { type: "complete" };
var emptySnapshotSingularUnknown = [void 0, resultTypeUnknown];
var emptySnapshotSingularComplete = [void 0, resultTypeComplete];
var emptySnapshotPluralUnknown = [emptyArray, resultTypeUnknown];
var emptySnapshotPluralComplete = [emptyArray, resultTypeComplete];
function getDefaultSnapshot(singular) {
  return singular ? emptySnapshotSingularUnknown : emptySnapshotPluralUnknown;
}
function getSnapshot(singular, data, resultType) {
  if (singular && data === void 0) {
    return resultType === "complete" ? emptySnapshotSingularComplete : emptySnapshotSingularUnknown;
  }
  if (!singular && data.length === 0) {
    return resultType === "complete" ? emptySnapshotPluralComplete : emptySnapshotPluralUnknown;
  }
  return [
    data,
    resultType === "complete" ? resultTypeComplete : resultTypeUnknown
  ];
}
var ViewStore = class {
  #views = /* @__PURE__ */ new Map();
  constructor() {
    if (false) {
      allViews.set(this, this.#views);
    }
  }
  getView(clientID, query, enabled, ttl) {
    const { format } = query;
    if (!enabled) {
      return {
        getSnapshot: () => getDefaultSnapshot(format.singular),
        subscribeReactInternals: disabledSubscriber,
        updateTTL: () => {
        }
      };
    }
    const hash = query.hash() + clientID;
    let existing = this.#views.get(hash);
    if (!existing) {
      existing = new ViewWrapper(
        query,
        format,
        ttl,
        (view) => {
          const lastView = this.#views.get(hash);
          if (lastView && lastView !== view) {
            throw new Error("View already exists");
          }
          this.#views.set(hash, view);
        },
        () => {
          this.#views.delete(hash);
        }
      );
      this.#views.set(hash, existing);
    } else {
      existing.updateTTL(ttl);
    }
    return existing;
  }
};
var viewStore = new ViewStore();
var ViewWrapper = class {
  #view;
  #onDematerialized;
  #onMaterialized;
  #query;
  #format;
  #snapshot;
  #reactInternals;
  #ttl;
  constructor(query, format, ttl, onMaterialized, onDematerialized) {
    this.#query = query;
    this.#format = format;
    this.#ttl = ttl;
    this.#onMaterialized = onMaterialized;
    this.#onDematerialized = onDematerialized;
    this.#snapshot = getDefaultSnapshot(format.singular);
    this.#reactInternals = /* @__PURE__ */ new Set();
    this.#materializeIfNeeded();
  }
  #onData = (snap, resultType) => {
    const data = snap === void 0 ? snap : deepClone(snap);
    this.#snapshot = getSnapshot(this.#format.singular, data, resultType);
    for (const internals of this.#reactInternals) {
      internals();
    }
  };
  #materializeIfNeeded = () => {
    if (this.#view) {
      return;
    }
    this.#view = this.#query.materialize(this.#ttl);
    this.#view.addListener(this.#onData);
    this.#onMaterialized(this);
  };
  getSnapshot = () => this.#snapshot;
  subscribeReactInternals = (internals) => {
    this.#reactInternals.add(internals);
    this.#materializeIfNeeded();
    return () => {
      this.#reactInternals.delete(internals);
      if (this.#reactInternals.size === 0) {
        setTimeout(() => {
          if (this.#reactInternals.size > 0) {
            return;
          }
          if (this.#view === void 0) {
            return;
          }
          this.#view?.destroy();
          this.#view = void 0;
          this.#onDematerialized();
        }, 10);
      }
    };
  };
  updateTTL(ttl) {
    this.#ttl = ttl;
    this.#view?.updateTTL(ttl);
  }
};
export {
  ZeroProvider,
  createUseZero,
  useQuery,
  useZero
};
//# sourceMappingURL=react.js.map
